/**
 * Always-on console trace for the "send-but-don't-render" recurring bug.
 *
 * Symptom: user sends a message, mutator fires, server stores it (other
 * clients see it), but the local message list does NOT show it until
 * the app is reloaded.
 *
 * Capture strategy:
 *   1. `markSent(msgId, channelId, kind)` is called from `useMessageSend`
 *      the moment we hand a row to `zero.mutate(...)`. The id is added
 *      to a per-channel outbox with a wall-clock timestamp.
 *   2. `markVisible(channelId, messageList)` is called from
 *      `useChannelMessageList` whenever the rendered slice changes.
 *      For every outbox id now visible, we log `LANDED` with the
 *      elapsed time and remove it from the outbox.
 *   3. A 5s setTimeout per send fires `BUG?` when the id has not landed
 *      locally — this is the loud signal you can grep for in the
 *      console after the symptom hits.
 *
 * Logs are unconditional (NOT gated behind `RENDER_DEBUG`) so the trace
 * is captured the first time the bug recurs, without anyone needing
 * to flip a flag. They are low-volume — 1-2 lines per send — so the
 * production cost is negligible.
 *
 * Tag prefixes (greppable):
 *   [send-trace] SENT    msgId=… channelId=… kind=…
 *   [send-trace] LANDED  msgId=… t+Nms
 *   [send-trace] BUG?    msgId=… not visible after 5000ms (channelId=…, ageMs=…)
 *
 * Once the bug is captured we can correlate the SENT/BUG? lines with
 * the surrounding `[zq:messages.byChannelComposite]` IVM emissions to
 * see whether the row arrived in the snapshot at all (Zero never
 * delivered it locally) vs. arrived but was filtered out by something
 * downstream.
 */

interface OutboxEntry {
  channelId: string;
  kind: string;
  sentAt: number;
  bugTimer: ReturnType<typeof setTimeout> | null;
}

const outbox = new Map<string, OutboxEntry>();

const BUG_THRESHOLD_MS = 5000;

/**
 * Call as close to `zero.mutate(...)` as possible, with the id we
 * generated for the optimistic insert.
 */
export function markSent(
  msgId: string,
  channelId: string,
  kind: "insert" | "addReply" | "giphy" | string,
): void {
  const now = Date.now();
  // If a previous timer is still alive for this id (extremely unusual —
  // would mean the same client uuid was reused), clear it first so we
  // don't leak.
  const existing = outbox.get(msgId);
  if (existing?.bugTimer) clearTimeout(existing.bugTimer);

  const bugTimer = setTimeout(() => {
    const entry = outbox.get(msgId);
    if (!entry) return;
    const ageMs = Date.now() - entry.sentAt;
    console.warn(
      `[send-trace] BUG? msgId=${msgId} not visible after ${BUG_THRESHOLD_MS}ms`,
      `(channelId=${entry.channelId}, ageMs=${ageMs})`,
    );
    // Don't drop the entry — keep it so a later `markVisible` can still
    // log a delayed LANDED, which itself is useful diagnostic data.
    entry.bugTimer = null;
  }, BUG_THRESHOLD_MS);

  outbox.set(msgId, { channelId, kind, sentAt: now, bugTimer });
  console.debug(
    `[send-trace] SENT msgId=${msgId} channelId=${channelId} kind=${kind}`,
  );
}

/**
 * Call from the render path with the active channel and a `.has(id)`
 * container of the currently-rendered message ids — most callers
 * already have a `Map<string, …>` keyed by id (e.g. `messageById`),
 * which satisfies `ReadonlyMap` and avoids any per-render allocation
 * just to check membership. Logs a LANDED line for every outbox id
 * that is now visible and removes it from the outbox.
 */
export function markVisible(
  channelId: string | null,
  messageIds: ReadonlyMap<string, unknown> | ReadonlySet<string>,
): void {
  if (!channelId || outbox.size === 0) return;
  const now = Date.now();
  for (const [msgId, entry] of outbox) {
    if (entry.channelId !== channelId) continue;
    if (!messageIds.has(msgId)) continue;
    const elapsed = now - entry.sentAt;
    console.debug(`[send-trace] LANDED msgId=${msgId} t+${elapsed}ms`);
    if (entry.bugTimer) clearTimeout(entry.bugTimer);
    outbox.delete(msgId);
  }
}
